import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { useToasts } from "@air/provider-toast";
import classNames from "classnames";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrivateBoardsDeleteBoardCoverMutation } from "~/components/v2/hooks/queries/mutations/usePrivateBoardsDeleteBoardCoverMutation";
import { usePrivateBoardsSetBoardCoverMutation } from "~/components/v2/hooks/queries/mutations/usePrivateBoardsSetBoardCoverMutation";
const POSITION_STEP = 1;
const POSITION_STEP_LARGE = 5;
const DEFAULT_POSITION = 50;
export const BOARD_COVER_EDITOR_X_POSITION_CSS_VAR = "--board-cover-editor-x-position";
export const BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR = "--board-cover-editor-y-position";
const calculatePositionFromRegion = (region)=>{
    return 100 - region.height === 0 ? DEFAULT_POSITION : region.top / (100 - region.height) * 100;
};
export const BoardCoverEditor = /*#__PURE__*/ memo((param)=>{
    let { board, imgRef } = param;
    var _board_coverImage, _board_coverImage1;
    const { mutate: deleteBoardCoverMutation } = usePrivateBoardsDeleteBoardCoverMutation();
    const { mutate: setBoardCoverMutation } = usePrivateBoardsSetBoardCoverMutation();
    const { showToast } = useToasts();
    const [isDragging, setIsDragging] = useState(false);
    const [isRepositioning, setIsRepositioning] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const coverRef = useRef(null);
    const overlayRef = useRef(null);
    const repositionButtonRef = useRef(null);
    const dragStartYRef = useRef(0);
    const initialPositionRef = useRef(0);
    const currentPosition = useMemo(()=>{
        var _board_coverImage;
        return ((_board_coverImage = board.coverImage) === null || _board_coverImage === void 0 ? void 0 : _board_coverImage.region) ? calculatePositionFromRegion(board.coverImage.region) : DEFAULT_POSITION;
    }, [
        (_board_coverImage = board.coverImage) === null || _board_coverImage === void 0 ? void 0 : _board_coverImage.region
    ]);
    useEffect(()=>{
        const img = imgRef.current;
        if (!img) {
            setImageLoaded(false);
            return;
        }
        if (img.complete && img.naturalHeight !== 0) {
            setImageLoaded(true);
        } else {
            setImageLoaded(false);
            const handleLoad = ()=>setImageLoaded(true);
            img.addEventListener("load", handleLoad);
            return ()=>{
                img.removeEventListener("load", handleLoad);
            };
        }
    }, [
        imgRef,
        (_board_coverImage1 = board.coverImage) === null || _board_coverImage1 === void 0 ? void 0 : _board_coverImage1.url
    ]);
    useEffect(()=>{
        const handlePointerMove = (e)=>{
            var _coverRef_current;
            if (!isDragging || !coverRef.current || !imgRef.current) return;
            e.preventDefault();
            const deltaY = e.clientY - dragStartYRef.current;
            const containerHeight = coverRef.current.offsetHeight;
            const naturalHeight = imgRef.current.naturalHeight;
            const naturalWidth = imgRef.current.naturalWidth;
            const containerWidth = coverRef.current.offsetWidth;
            const imageAspect = naturalHeight / naturalWidth;
            const containerAspect = containerHeight / containerWidth;
            let scaledImageHeight;
            if (imageAspect > containerAspect) {
                scaledImageHeight = containerWidth * imageAspect;
            } else {
                scaledImageHeight = containerHeight;
            }
            const overflowHeight = scaledImageHeight - containerHeight;
            if (overflowHeight <= 0) {
                return;
            }
            const dragPercentage = -deltaY / overflowHeight * 100;
            let newPosition = initialPositionRef.current + dragPercentage;
            newPosition = Math.max(0, Math.min(100, newPosition));
            const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
            if (parentContainer instanceof HTMLElement) {
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, "".concat(newPosition, "%"));
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, "".concat(DEFAULT_POSITION, "%"));
            }
        };
        const handlePointerUp = ()=>{
            setIsDragging(false);
            document.body.style.cursor = "default";
        };
        if (isDragging) {
            document.addEventListener("pointermove", handlePointerMove);
            document.addEventListener("pointerup", handlePointerUp);
            document.body.style.cursor = "ns-resize";
        }
        return ()=>{
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            document.body.style.cursor = "default";
        };
    }, [
        isDragging,
        imgRef
    ]);
    const handleDragStart = useCallback((e)=>{
        var _coverRef_current;
        if (!isRepositioning) return;
        e.preventDefault();
        setIsDragging(true);
        dragStartYRef.current = e.clientY;
        const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
        if (parentContainer instanceof HTMLElement) {
            const currentPos = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
            initialPositionRef.current = currentPos ? parseFloat(currentPos.replace("%", "")) : currentPosition;
        }
    }, [
        isRepositioning,
        currentPosition
    ]);
    const removeCover = useCallback(()=>{
        deleteBoardCoverMutation({
            boardId: board.id,
            workspaceId: board.workspaceId,
            board,
            trackLocation: "private-gallery-page"
        }, {
            onSuccess: ()=>{
                showToast("Board cover removed successfully");
            },
            onError: ()=>{
                showToast("Failed to remove board cover. Please try again.");
            }
        });
    }, [
        board,
        deleteBoardCoverMutation,
        showToast
    ]);
    const calculateRegion = useCallback(()=>{
        var _coverRef_current;
        if (!imgRef.current || !coverRef.current) return null;
        const containerWidth = coverRef.current.offsetWidth;
        const containerHeight = coverRef.current.offsetHeight;
        const naturalWidth = imgRef.current.naturalWidth;
        const naturalHeight = imgRef.current.naturalHeight;
        if (!naturalWidth || !naturalHeight) return null;
        const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
        let vPosition = DEFAULT_POSITION;
        let hPosition = DEFAULT_POSITION;
        if (parentContainer instanceof HTMLElement) {
            const vPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
            const hPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);
            if (vPosValue) {
                vPosition = parseFloat(vPosValue.replace("%", ""));
            }
            if (hPosValue) {
                hPosition = parseFloat(hPosValue.replace("%", ""));
            }
        }
        const imageAspect = naturalHeight / naturalWidth;
        const containerAspect = containerHeight / containerWidth;
        let region = {
            top: 0,
            left: 0,
            width: 100,
            height: 100
        };
        if (imageAspect > containerAspect) {
            const scaleFactor = containerWidth / naturalWidth;
            const scaledHeight = naturalHeight * scaleFactor;
            const visibleHeightPercent = containerHeight / scaledHeight * 100;
            region = {
                top: vPosition * (100 - visibleHeightPercent) / 100,
                left: 0,
                width: 100,
                height: visibleHeightPercent
            };
        } else {
            const scaleFactor = containerHeight / naturalHeight;
            const scaledWidth = naturalWidth * scaleFactor;
            const visibleWidthPercent = containerWidth / scaledWidth * 100;
            region = {
                top: 0,
                left: hPosition * (100 - visibleWidthPercent) / 100,
                width: visibleWidthPercent,
                height: 100
            };
        }
        return region;
    }, [
        imgRef
    ]);
    const handleSavePosition = useCallback(()=>{
        const region = calculateRegion();
        if (region && board.coverImage) {
            setBoardCoverMutation({
                boardId: board.id,
                workspaceId: board.workspaceId,
                region,
                clipId: board.coverImage.clipId,
                thumbnailUrl: board.coverImage.url,
                board,
                trackLocation: "private-gallery-page"
            }, {
                onSuccess: ()=>{
                    var _coverRef_current;
                    showToast("Cover position saved");
                    const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
                    if (parentContainer instanceof HTMLElement) {
                        parentContainer.style.removeProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
                        parentContainer.style.removeProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);
                    }
                    setIsRepositioning(false);
                    setTimeout(()=>{
                        var _repositionButtonRef_current;
                        return (_repositionButtonRef_current = repositionButtonRef.current) === null || _repositionButtonRef_current === void 0 ? void 0 : _repositionButtonRef_current.focus();
                    }, 0);
                },
                onError: ()=>{
                    showToast("Failed to save position. Please try again.");
                }
            });
        }
    }, [
        calculateRegion,
        board,
        setBoardCoverMutation,
        showToast
    ]);
    const handleCancel = useCallback(()=>{
        var _coverRef_current;
        const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
        if (parentContainer instanceof HTMLElement) {
            parentContainer.style.removeProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
            parentContainer.style.removeProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR);
        }
        setIsRepositioning(false);
        setTimeout(()=>{
            var _repositionButtonRef_current;
            return (_repositionButtonRef_current = repositionButtonRef.current) === null || _repositionButtonRef_current === void 0 ? void 0 : _repositionButtonRef_current.focus();
        }, 0);
    }, []);
    const handleStartReposition = useCallback(()=>{
        if (imageLoaded) {
            var _coverRef_current;
            const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
            if (parentContainer instanceof HTMLElement) {
                var _board_coverImage;
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, "".concat(currentPosition, "%"));
                var _board_coverImage_region;
                const region = (_board_coverImage_region = (_board_coverImage = board.coverImage) === null || _board_coverImage === void 0 ? void 0 : _board_coverImage.region) !== null && _board_coverImage_region !== void 0 ? _board_coverImage_region : {
                    top: 0,
                    left: 0,
                    width: 100,
                    height: 100
                };
                const horizontalPosition = region.width >= 100 ? DEFAULT_POSITION : region.left / (100 - region.width) * 100;
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, "".concat(horizontalPosition, "%"));
            }
            setIsRepositioning(true);
        }
    }, [
        imageLoaded,
        currentPosition,
        board.coverImage
    ]);
    useEffect(()=>{
        const overlay = overlayRef.current;
        const handleKeyDown = (e)=>{
            var _coverRef_current;
            if (!isRepositioning || !coverRef.current) return;
            let delta = 0;
            const step = e.shiftKey ? POSITION_STEP_LARGE : POSITION_STEP;
            if (e.key === "ArrowUp") {
                delta = -step;
            } else if (e.key === "ArrowDown") {
                delta = step;
            } else if (e.key === "Escape") {
                handleCancel();
                return;
            } else if (e.key === "Enter") {
                handleSavePosition();
                return;
            }
            if (delta === 0) return;
            e.preventDefault();
            const parentContainer = (_coverRef_current = coverRef.current) === null || _coverRef_current === void 0 ? void 0 : _coverRef_current.closest(".group\\/board-cover");
            let currentPos = currentPosition;
            if (parentContainer instanceof HTMLElement) {
                const vPosValue = parentContainer.style.getPropertyValue(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR);
                currentPos = vPosValue ? parseFloat(vPosValue.replace("%", "")) : currentPosition;
            }
            let newPosition = currentPos + delta;
            newPosition = Math.max(0, Math.min(100, newPosition));
            if (parentContainer instanceof HTMLElement) {
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR, "".concat(newPosition, "%"));
                parentContainer.style.setProperty(BOARD_COVER_EDITOR_X_POSITION_CSS_VAR, "".concat(DEFAULT_POSITION, "%"));
            }
            if (overlay) {
                overlay.setAttribute("aria-valuenow", newPosition.toString());
            }
        };
        if (isRepositioning) {
            overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener("keydown", handleKeyDown);
            setTimeout(()=>overlay === null || overlay === void 0 ? void 0 : overlay.focus(), 0);
        }
        return ()=>{
            overlay === null || overlay === void 0 ? void 0 : overlay.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        isRepositioning,
        currentPosition,
        handleCancel,
        handleSavePosition
    ]);
    if (!board.coverImage) {
        return null;
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            ref: coverRef,
            className: "absolute inset-0 overflow-hidden rounded-8 focus-visible:ring-2 focus-visible:ring-blue-9",
            "data-disableselect": "true",
            tabIndex: 0,
            "aria-label": "Board cover image. Press Tab to access controls",
            children: [
                isRepositioning && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            ref: overlayRef,
                            className: "absolute inset-0 z-10 cursor-ns-resize touch-none rounded-8 shadow-[inset_0_0_0_3px_rgba(255,255,255,0.4)] outline-none focus:ring-2 focus:ring-blue-9",
                            onPointerDown: handleDragStart,
                            tabIndex: 0,
                            role: "slider",
                            "aria-orientation": "vertical",
                            "aria-valuemin": 0,
                            "aria-valuemax": 100,
                            "aria-valuenow": Math.round(currentPosition),
                            "aria-label": "Adjust cover image vertical position"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "pointer-events-none absolute inset-0 z-20 flex items-center justify-center",
                            children: /*#__PURE__*/ _jsx("span", {
                                className: "rounded-8 bg-grey-1/70 px-3 py-2 text-12 text-grey-12",
                                children: "Drag or use arrow keys to reposition"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: classNames("absolute bottom-4 left-4 z-20 gap-2", isRepositioning ? "flex" : "hidden group-focus-within/board-cover:flex group-hover/board-cover:flex"),
                    children: isRepositioning ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "grey",
                                size: "small",
                                onClick: handleSavePosition,
                                children: "Save position"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "grey",
                                size: "small",
                                onClick: handleCancel,
                                children: "Cancel"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                ref: repositionButtonRef,
                                appearance: "filled",
                                color: "grey",
                                size: "small",
                                onClick: handleStartReposition,
                                disabled: !imageLoaded,
                                children: "Reposition cover"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "grey",
                                size: "small",
                                onClick: removeCover,
                                disabled: !imageLoaded,
                                children: "Remove"
                            })
                        ]
                    })
                })
            ]
        })
    });
});
BoardCoverEditor.displayName = "BoardCoverEditor";
